/**
 * 数值格式化工具函数
 *
 * 将数值格式化为指定格式的字符串，支持多种格式化规则：
 * - M格式：保留小数位数，如 'M.2'（保留2位小数），'M'（不保留小数）
 * - W格式：万单位转换，如 'W.2'（保留2位小数），'W.1'（保留1位小数）
 * - Y格式：亿单位转换，如 'Y.2'（保留2位小数），'Y.1'（保留1位小数）
 * - Amount格式：自动单位判断，如 'Amount.2'（根据数值大小自动选择万、亿或原值）
 * - 百分号格式：如 '+M.2%'（带正号且显示百分号）
 * - 组合格式：如 '+W.2%'（万单位+正号+百分比），'Y.1'（亿单位+1位小数）
 * - 自定义函数：可传入自定义格式化函数
 *
 * @param value - 需要格式化的值
 * @param format - 格式化规则，字符串格式或自定义函数
 * @param fallback - 当输入值无效时的返回值，默认为 '--'
 * @returns 格式化后的字符串
 *
 * @example
 * ```ts
 * import { formatNumber } from '@fu/matrix';
 *
 * // 基本格式化
 * formatNumber(123.456, 'M.2'); // => '123.46'
 * formatNumber(123.456, 'M.0'); // => '123'
 * formatNumber(123.456, 'M'); // => '123.456' (不保留小数)
 *
 * // 字符串原始格式支持（仅M格式且不指定精度时）
 * formatNumber('1.0', '+M'); // => '+1.0' (保持原始字符串格式)
 * formatNumber('123.456', 'M'); // => '123.456' (保持原始字符串格式)
 * formatNumber('1.0', 'M.2'); // => '1.00' (指定精度时进行数值格式化)
 *
 * // 万单位转换
 * formatNumber(123456, 'W.2'); // => '12.35万'
 * formatNumber(123456, 'W.1'); // => '12.3万'
 * formatNumber(123456, 'W'); // => '12.3456万'
 * formatNumber(123456, '+W.2'); // => '+12.35万'
 *
 * // 亿单位转换
 * formatNumber(1234567890, 'Y.2'); // => '12.35亿'
 * formatNumber(1234567890, 'Y.1'); // => '12.3亿'
 * formatNumber(1234567890, 'Y'); // => '12.3456789亿'
 * formatNumber(1234567890, '+Y.2'); // => '+12.35亿'
 *
 * // Amount 自动单位判断
 * formatNumber(1234567890, 'Amount.2'); // => '12.35亿' (>=1亿，自动用亿)
 * formatNumber(123456, 'Amount.2'); // => '12.35万' (>=1万，自动用万)
 * formatNumber(1234, 'Amount.2'); // => '1234' (<1万，保持原值，不应用精度)
 * formatNumber(0, 'Amount.2'); // => '0' (<1万，保持原值，不应用精度)
 *
 * // 百分号格式化
 * formatNumber(0.1234, 'M.2%'); // => '12.34%'
 * formatNumber(0.1234, '+M.2%'); // => '+12.34%'
 * formatNumber(0.1234, 'W.2%'); // => '12.34万%'
 * formatNumber(0.1234, 'Y.2%'); // => '12.34亿%'
 *
 * // 自定义函数
 * formatNumber(123.456, (val) => `$${val.toFixed(2)}`); // => '$123.46'
 *
 * // 无效值处理
 * formatNumber(null, 'M.2'); // => '--'
 * formatNumber('invalid', 'M.2', 'N/A'); // => 'N/A'
 * ```
 *
 * @since 1.0.0
 */

// 验证格式参数
function validateFormat(format: unknown): void {
  if (format === null || format === undefined) {
    throw new TypeError('format 参数不能为空');
  }
  if (typeof format !== 'string' && typeof format !== 'function') {
    throw new TypeError('format 参数必须是字符串或函数');
  }
}

// 严格的四舍五入函数
function roundToPrecision(value: number, precision: number): number {
  if (precision === 0) {
    return Math.round(value);
  }
  const multiplier = Math.pow(10, precision);
  return Math.round(value * multiplier) / multiplier;
}

// 将数值格式化为指定精度的字符串
function formatToPrecision(value: number, precision: number): string {
  if (!isFinite(value)) {
    return value.toString();
  }
  
  const rounded = roundToPrecision(value, precision);
  
  if (precision === 0) {
    return rounded.toString();
  }
  
  // 使用 toFixed 确保正确的小数位数显示
  return rounded.toFixed(precision);
}

// 将输入值转换为数字
function convertToNumber(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return isNaN(value) ? null : value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }
    const num = Number(trimmed);
    return isNaN(num) ? null : num;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  const num = Number(value);
  return isNaN(num) ? null : num;
}

// 检查字符串是否为有效的数字字符串（保持原始格式）
function isValidNumberString(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') {
    return false;
  }
  const num = Number(trimmed);
  return !isNaN(num) && isFinite(num);
}

// 获取字符串的原始数字值（用于判断正负号）
function getStringNumberValue(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const num = Number(trimmed);
  return isNaN(num) ? null : num;
}

// 解析格式字符串
function parseFormatString(format: string): {
  type: 'M' | 'W' | 'Y' | 'Amount';
  precision: number | null;
  showPlus: boolean;
  showPercent: boolean;
} | null {
  const match = format.match(/^(\+?)(M|W|Y|Amount)(?:\.(\d+))?(%?)$/);
  if (!match) {
    return null;
  }
  const [, plusSign, type, precisionStr, percentSign] = match;
  return {
    type: type as 'M' | 'W' | 'Y' | 'Amount',
    precision: precisionStr ? parseInt(precisionStr, 10) : null,
    showPlus: plusSign === '+',
    showPercent: percentSign === '%',
  };
}

// 单位转换函数
function convertUnit(value: number, type: 'M' | 'W' | 'Y' | 'Amount'): { value: number; unit: string } {
  switch (type) {
    case 'M':
      return { value, unit: '' };
    case 'W':
      return { value: value / 10000, unit: '万' };
    case 'Y':
      return { value: value / 100000000, unit: '亿' };
    case 'Amount':
      // Amount 格式：根据数值大小自动选择单位
      if (Math.abs(value) >= 100000000) {
        // 大于等于1亿，使用亿单位
        return { value: value / 100000000, unit: '亿' };
      } else if (Math.abs(value) >= 10000) {
        // 大于等于1万，使用万单位
        return { value: value / 10000, unit: '万' };
      } else {
        // 小于1万，保持原值（不进行单位转换）
        return { value, unit: '' };
      }
    default:
      return { value, unit: '' };
  }
}

// 使用字符串格式进行格式化
function formatWithString(value: number, format: string, fallback: string, originalValue?: unknown): string {
  try {
    const parsed = parseFormatString(format);
    if (!parsed) {
      return fallback;
    }

    const { type, precision, showPlus, showPercent } = parsed;
    
    // 如果是字符串输入且是M格式（不涉及单位转换），尝试保持原始字符串格式
    if (originalValue && typeof originalValue === 'string' && type === 'M' && !showPercent) {
      const trimmedOriginal = originalValue.trim();
      if (isValidNumberString(trimmedOriginal)) {
        const numValue = getStringNumberValue(trimmedOriginal);
        if (numValue !== null) {
          // 对于M格式，如果精度为null（不指定精度），保持原始字符串格式
          if (precision === null) {
            let result = trimmedOriginal;
            
            // 添加正号
            if (showPlus && numValue > 0) {
              result = '+' + result;
            }
            
            return result;
          }
        }
      }
    }
    
    // 单位转换
    const { value: convertedValue, unit } = convertUnit(value, type);
    
    // 百分比处理
    let result = showPercent ? convertedValue * 100 : convertedValue;

    // 精度格式化
    let str: string;
    if (precision !== null) {
      // 对于 Amount 格式且小于1万的情况，直接返回原值不应用精度
      if (type === 'Amount' && Math.abs(value) < 10000) {
        str = value.toString();
      } else {
        str = formatToPrecision(result, precision);
      }
    } else {
      str = result.toString();
    }

    // 添加正号
    if (showPlus && result > 0) {
      str = '+' + str;
    }
    
    // 添加百分比符号
    if (showPercent) {
      str += '%';
    }
    
    // 添加单位
    if (unit) {
      str += unit;
    }

    return str;
  } catch (error) {
    console.warn('格式化失败:', error);
    return fallback;
  }
}

// 使用自定义函数进行格式化
function formatWithFunction(value: number, format: (value: number) => string, fallback: string): string {
  try {
    return format(value);
  } catch (error) {
    console.warn('自定义格式化函数执行失败:', error);
    return fallback;
  }
}

export function formatNumber(
  value: unknown,
  format: string | ((value: number) => string),
  fallback: string = '--'
): string {
  validateFormat(format);
  
  const numValue = convertToNumber(value);
  if (numValue === null) {
    return fallback;
  }

  if (typeof format === 'function') {
    return formatWithFunction(numValue, format, fallback);
  }
  
  return formatWithString(numValue, format, fallback, value);
} 